import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import Link from 'components/Link';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import useViewport from 'hooks/useViewport';
import { handleScrollTo } from 'screens/compare/components/NavigationList/helpers';

import PPLBanner from './components/PPLBanner';
import PPLBannerVariant from './components/PPLBannerVariant';

import classes from './TableOfContent.module.scss';

/** @type {import("./types").default} */
const TableOfContent = ({
  content,
  stickyContainerRef = null,
  showPplPricingBanner = false,
}) => {
  const { addEvent } = useEventContext();
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [showPPLBanner, setShowPPLBanner] = useState(showPplPricingBanner);
  const { isCypressTakingSnapshots, head } = useAppContext();
  const hasPPLDemoForm = head?.hasPplDemoFormEnabled;
  const { up } = useViewport();
  const isDesktop = up('md');

  useEffect(() => {
    if (!isDesktop) return;
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target?.id) {
          setSelectedMenuItem(entry.target.id);
          return;
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-25% 0px -40% 0px',
    });

    document
      .querySelectorAll('.ContentItem')
      .forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [isDesktop]);

  const defaultRef = useRef();
  const containerRef = stickyContainerRef || defaultRef;

  useEffect(() => {
    if (isDesktop && !isCypressTakingSnapshots) {
      const setSticky = async () => {
        const StickyFilter = (
          await import('components/Filters/stickyFilter.js')
        ).default;
        // This is needed because after waiting for the import sometimes the ref is null
        if (containerRef.current)
          new StickyFilter(containerRef.current, {
            topSpacing: 94,
            bottomSpacing: 80,
          });
      };

      if (containerRef.current) {
        setSticky();
      }

      if (!showPplPricingBanner) {
        const handleScroll = () => {
          if (window.scrollY > 700) {
            setShowPPLBanner(true);
            window.removeEventListener('scroll', handleScroll);
          } else {
            setShowPPLBanner(false);
          }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
          window.removeEventListener('scroll', handleScroll);
        };
      }
    }
  }, [containerRef, isDesktop, isCypressTakingSnapshots, showPplPricingBanner]);

  return (
    <div className={classes.root} ref={defaultRef}>
      <Typography variant="heading-medium" className={classes.header}>
        Table of Contents
      </Typography>
      <div className={classes.itemsList}>
        {content.map(({ title = '', menuTitle = '', id }) => (
          <Link
            key={id}
            onClick={() => handleScrollTo(id, 100)}
            className={clsx(classes.item, {
              [classes.active]: selectedMenuItem === id,
            })}
            {...addEvent({
              name: 'engagement_navigation',
              engagementComponent: 'table-of-contents',
              engagementDestination: `jump-to_${id}`,
              engagementDetails: `text-link_${id}`,
            })}
          >
            {menuTitle || title}
          </Link>
        ))}
      </div>

      {hasPPLDemoForm &&
        isDesktop &&
        (showPplPricingBanner ? (
          <PPLBannerVariant show={showPPLBanner} />
        ) : (
          <PPLBanner show={showPPLBanner} />
        ))}
    </div>
  );
};

export default TableOfContent;
